import { AlertTriangle, ArrowRight, X, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BANNER_TONE } from '@/components/ui/types/banner-variants-enums';
import { Banner } from '@/components/ui/banner';
import { BUTTON_SIZE, BUTTON_VARIANT } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '@/components/ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { useDismissibleBanner } from '@/hooks/browser/use-dismissible-banner';
import { useEmqHealthStatus } from '@/features/workspace/hooks/use-emq-health-status';
import type { EMQ_AGGREGATED_STATUS } from '@/features/workspace/types/use-emq-health-status-enums';

const STATUS_CONFIG: Record<
  Extract<EMQ_AGGREGATED_STATUS, 'warning' | 'critical'>,
  {
    tone: BANNER_TONE;
    getMessage: (score: number | null) => string;
    buttonText: string;
    Icon: typeof AlertTriangle;
  }
> = {
  warning: {
    tone: BANNER_TONE.WARNING,
    getMessage: (score) =>
      `Your Meta Event Match Quality score${score ? ` (${score.toFixed(1)})` : ''} needs attention. This may affect your ad targeting.`,
    buttonText: 'View Data Quality',
    Icon: AlertTriangle,
  },
  critical: {
    tone: BANNER_TONE.DANGER,
    getMessage: (score) =>
      `Your Meta Event Match Quality score${score ? ` (${score.toFixed(1)})` : ''} is critical. Your ad targeting may be significantly impacted.`,
    buttonText: 'View Data Quality',
    Icon: XCircle,
  },
};

const IS_EMQ_HEALTH_BANNER_ENABLED = false;

export function EmqHealthBanner() {
  return IS_EMQ_HEALTH_BANNER_ENABLED ? <ActiveEmqHealthBanner /> : null;
}

export function ActiveEmqHealthBanner() {
  const { status, emqHealth, isLoading } = useEmqHealthStatus();
  const { isDismissed, dismiss } = useDismissibleBanner({
    bannerId: 'emq-health',
    signal: status,
  });

  if (isLoading || status === 'healthy' || status === 'unknown' || status === 'not_configured') {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  const config = STATUS_CONFIG[status];
  const { Icon } = config;
  const score = emqHealth?.overallScore ?? null;

  return (
    <Banner.Root role="alert" tone={config.tone}>
      <Banner.Content>
        <Banner.Icon>
          <Icon />
        </Banner.Icon>
        <Banner.Message>{config.getMessage(score)}</Banner.Message>
      </Banner.Content>
      <Banner.Actions>
        <Button
          asChild
          className="gap-1.5"
          size={BUTTON_SIZE.BANNER}
          variant={BUTTON_VARIANT.PRIMARY}
        >
          <Link aria-label={config.buttonText} to="/meta-data-quality">
            <Banner.ActionLabel compact="Quality">{config.buttonText}</Banner.ActionLabel>
            <ArrowRight aria-hidden="true" className="hidden @[420px]:block" />
          </Link>
        </Button>
      </Banner.Actions>
      <Banner.Dismiss>
        <IconButton
          label="Dismiss EMQ banner"
          onClick={dismiss}
          size={ICON_BUTTON_SIZE.BANNER}
          variant={ICON_BUTTON_VARIANT.GHOST}
        >
          <X />
        </IconButton>
      </Banner.Dismiss>
    </Banner.Root>
  );
}

export default EmqHealthBanner;
